import React, { useEffect, useState } from 'react';
import { Col, Form, Input, Row, Select, Skeleton, Table, Tabs } from 'antd';
import { FundOutlined } from '@ant-design/icons';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import PersianDatePicker from '../../components/PersianDatePicker';
import ReportPageLayout from './ReportPageLayout';
import ReportFiltersCard from './ReportFiltersCard';
import { reportsApi } from '../../utils/api';
import { extractPagination, extractRows, getDateRangePickerValue, normalizeEnglishDigits, parseDateRangeChange, toNumber } from './reportUtils';
import { getLast30DaysRange } from '../../utils/dateUtils';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import ChartEmptyState from './ChartEmptyState';
import { formatReportNumberFa, REPORT_CHART_COLORS } from './chartFormatters';
import { CountBarTooltip } from './ReportChartTooltips';

const LeadReport = () => {
  const range = getLast30DaysRange('jYYYY-MM-DD', 'jalali');
  const [chartFilters, setChartFilters] = useState({ pipeline: 'all', category: 'all', year: '1405' });
  const [tableFilters, setTableFilters] = useState({
    startDate: range.startDate,
    endDate: range.endDate,
    agent: 'all',
    page: 1,
    per_page: 15,
  });
  const [chartLoading, setChartLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [chartRows, setChartRows] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [apiFilters, setApiFilters] = useState({});
  const [exportLoading, setExportLoading] = useState(false);

  const loadChart = async () => {
    setChartLoading(true);
    try {
      const res = await reportsApi.getLeadReportChart(chartFilters);
      setChartRows(extractRows(res));
      setApiFilters((p) => ({ ...p, ...(res?.filters || {}) }));
    } finally {
      setChartLoading(false);
    }
  };

  const loadTable = async (next = tableFilters) => {
    if (!next.startDate || !next.endDate) return;
    setTableLoading(true);
    try {
      const res = await reportsApi.getLeadReportList(next);
      setTableRows(extractRows(res));
      setPagination(extractPagination(res));
      setApiFilters((p) => ({ ...p, ...(res?.filters || {}) }));
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => loadChart(), 350);
    return () => clearTimeout(timer);
  }, [chartFilters]);

  useEffect(() => {
    const timer = setTimeout(() => loadTable(tableFilters), 350);
    return () => clearTimeout(timer);
  }, [tableFilters]);

  return (
    <ReportPageLayout
      headerIcon={<FundOutlined />}
      title="گزارش معاملات"
      subtitle="نمودار قیف و لیست معاملات بر اساس بازه زمانی"
      onExport={async () => {
        setExportLoading(true);
        try {
          await reportsApi.exportLeadReport(chartFilters);
        } finally {
          setExportLoading(false);
        }
      }}
      exportLoading={exportLoading}
    >
      <Tabs
        className="custom-font"
        items={[
          {
            key: 'chart',
            label: 'نمودار',
            children: (
              <>
                <ReportFiltersCard title="فیلتر نمودار">
                  <Form layout="vertical" className="custom-font">
                    <Row gutter={[16, 12]}>
                      <Col xs={24} md={8}>
                        <Form.Item label="پایپ‌لاین" className="mb-0">
                          <Select
                            value={chartFilters.pipeline}
                            onChange={(v) => setChartFilters((p) => ({ ...p, pipeline: v }))}
                            options={[{ value: 'all', label: 'همه' }, ...(apiFilters?.pipelines || []).map((i) => ({ value: i.id, label: i.name }))]}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item label="دسته‌بندی" className="mb-0">
                          <Select
                            value={chartFilters.category}
                            onChange={(v) => setChartFilters((p) => ({ ...p, category: v }))}
                            options={[{ value: 'all', label: 'همه' }, ...(apiFilters?.categories || []).map((i) => ({ value: i.id, label: i.name }))]}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item label="سال (شمسی)" className="mb-0">
                          <Input
                            value={chartFilters.year}
                            onChange={(e) =>
                              setChartFilters((p) => ({ ...p, year: normalizeEnglishDigits(e.target.value || '') }))
                            }
                            placeholder="مثال: 1405"
                            maxLength={4}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </ReportFiltersCard>
                <div className="min-h-[260px] mb-2" dir="ltr">
                  {chartLoading && !chartRows.length ? (
                    <Skeleton active paragraph={{ rows: 8 }} />
                  ) : chartRows.length ? (
                    <div className="h-60 w-full pl-0 sm:pl-1">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={chartRows.map((r, i) => ({
                            label: r?.label || r?.stage || String(i + 1),
                            value: toNumber(r?.count || r?.value),
                          }))}
                          margin={{ top: 6, right: 10, left: 0, bottom: 4 }}
                        >
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} tickMargin={6} interval="preserveStartEnd" />
                          <YAxis
                            width={88}
                            tick={{ fontSize: 11 }}
                            tickFormatter={(v) => formatReportNumberFa(v)}
                            tickMargin={4}
                          />
                          <Tooltip content={<CountBarTooltip />} />
                          <Bar
                            dataKey="value"
                            name="تعداد"
                            fill={REPORT_CHART_COLORS.sky}
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <ChartEmptyState minHeight={300} />
                  )}
                </div>
              </>
            ),
          },
          {
            key: 'table',
            label: 'لیست',
            children: (
              <>
                <ReportFiltersCard title="فیلتر لیست">
                  <Form layout="vertical" className="custom-font">
                    <Row gutter={[16, 12]}>
                      <Col xs={24} lg={12}>
                        <Form.Item label="بازه تاریخ" className="mb-0">
                          <PersianDatePicker
                            range
                            placeholder="انتخاب بازه از تاریخ تا تاریخ"
                            value={getDateRangePickerValue(tableFilters.startDate, tableFilters.endDate)}
                            onChange={(dates) =>
                              setTableFilters((p) => ({ ...p, ...parseDateRangeChange(dates), page: 1 }))
                            }
                            style={{ minHeight: 36 }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item label="کارشناس" className="mb-0">
                          <Select
                            value={tableFilters.agent}
                            onChange={(v) => setTableFilters((p) => ({ ...p, agent: v, page: 1 }))}
                            options={[{ value: 'all', label: 'همه' }, ...(apiFilters?.agents || []).map((i) => ({ value: i.id, label: i.name }))]}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </ReportFiltersCard>
                {tableLoading ? (
                  <TableSkeleton rows={6} columns={5} />
                ) : (
                  <Table
                    rowKey={(r, i) => r.id || i}
                    dataSource={tableRows}
                    scroll={{ x: 1000 }}
                    className="custom-font"
                    pagination={{
                      ...pagination,
                      onChange: (page, pageSize) => {
                        const next = { ...tableFilters, page, per_page: pageSize };
                        setTableFilters(next);
                      },
                    }}
                    columns={[
                      { title: 'عنوان معامله', dataIndex: 'name' },
                      { title: 'مشتری', render: (_, r) => r?.client_name || '-' },
                      { title: 'مرحله', render: (_, r) => r?.pipeline_stage || r?.stage || '-' },
                      { title: 'مبلغ', render: (_, r) => r?.value || '-' },
                      { title: 'کارشناس', render: (_, r) => r?.agent_name || '-' },
                    ]}
                  />
                )}
              </>
            ),
          },
        ]}
      />
    </ReportPageLayout>
  );
};

export default LeadReport;
